import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import {
  CarrierOrdersGridService,
  DynamicGridConfigItem,
  DynamicGridUpdateTarget,
  IdentityScope,
} from '../services/carrier-orders-grid/carrier-orders-grid.service';
import { ModalConfig } from '../models/modal-system.interfaces';
import { GridModalDispatcherService } from '../services/carrier-orders-grid/grid-modal-dispatcher.service';

interface FieldDisplay {
  dbField: string;
  headerName: string;
  inputType: string;
  isEditable: boolean;
  value: any;
  displayValue: string;
  modalConfig?: ModalConfig;
  configItem: DynamicGridConfigItem;
}

interface FieldGroup {
  key: string;
  title: string;
  description: string;
  icon: string;
  accent: 'blue' | 'amber' | 'emerald' | 'rose' | 'slate';
  fields: FieldDisplay[];
}

interface SummaryItem {
  label: string;
  value: string;
  icon: string;
}

@Component({
  selector: 'app-carrier-order-details',
  templateUrl: './carrier-order-details.component.html',
  styleUrls: ['./carrier-order-details.component.scss'],
})
export class CarrierOrderDetailsComponent implements OnInit, OnDestroy {
  themeMode: 'dark' | 'light' = 'dark';
  ladingUuid = '';
  loading = true;
  saving = false;
  errorMessage = '';

  fields: FieldDisplay[] = [];
  groupedFields: FieldGroup[] = [];
  summaryItems: SummaryItem[] = [];
  editableFieldCount = 0;
  rowData: Record<string, any> = {};
  alternateRef = '';
  selectedCarrierContactUuid = '';
  identityScope: IdentityScope = 'carrier';

  editingField: string | null = null;
  editValue: any = '';
  togglingField: string | null = null;

  private dynamicConfig: DynamicGridConfigItem[] = [];
  private destroy$ = new Subject<void>();
  private get themeStorageKey(): string {
    return `${this.identityScope}-order-details-theme`;
  }

  constructor(
    private route: ActivatedRoute,
    private cwoService: CarrierOrdersGridService,
    private modalDispatcher: GridModalDispatcherService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.resolveIdentityScope();
    this.initializeTheme();
    this.ladingUuid = this.route.snapshot.paramMap.get('uuid') || '';
    if (!this.ladingUuid) {
      this.errorMessage = 'No lading UUID provided.';
      this.loading = false;
      return;
    }

    this.loadData();
  }

  private resolveIdentityScope(): void {
    const requestedScope = this.route.snapshot.queryParamMap.get('identity_scope');
    const validScopes: IdentityScope[] = ['carrier', 'customer', 'shipper', 'consignee'];
    if (validScopes.includes(requestedScope as IdentityScope)) {
      this.identityScope = requestedScope as IdentityScope;
      return;
    }

    this.identityScope = this.route.snapshot.routeConfig?.path?.startsWith('customer_')
      ? 'customer'
      : 'carrier';
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeTheme(): void {
    const storedTheme = window.localStorage.getItem(this.themeStorageKey);
    if (storedTheme === 'dark' || storedTheme === 'light') {
      this.themeMode = storedTheme;
      return;
    }

    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    this.themeMode = prefersDark ? 'dark' : 'light';
  }

  toggleTheme(): void {
    this.themeMode = this.themeMode === 'dark' ? 'light' : 'dark';
    window.localStorage.setItem(this.themeStorageKey, this.themeMode);
  }

  getThemeIcon(): string {
    return this.themeMode === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon';
  }

  getThemeLabel(): string {
    return this.themeMode === 'dark' ? 'Light' : 'Dark';
  }

  loadData(): void {
    this.loading = true;
    this.errorMessage = '';
    this.selectedCarrierContactUuid = this.route.snapshot.queryParamMap.get('contact_uuid') || '';

    this.cwoService.getDynamicWorkOrdersGrid(
      'work_orders',
      this.identityScope,
      this.selectedCarrierContactUuid || undefined,
    ).subscribe({
      next: (resp) => {
        this.loading = false;

        if (!resp || resp.success !== true) {
          this.errorMessage = resp?.message || 'Failed to load work order.';
          return;
        }

        this.dynamicConfig = (resp.config || [])
          .filter((c: any) => c && c.is_visible)
          .sort((a: any, b: any) => (a.display_order ?? 0) - (b.display_order ?? 0));

        const allData: any[] = resp.data || [];
        const row = allData.find((item: any) => item['lading_uuid'] === this.ladingUuid);

        if (!row) {
          this.errorMessage = 'Work order not found in data set.';
          return;
        }

        this.rowData = row;
        this.alternateRef = row['v_ladings.alternate_reference'] || '';
        this.buildFieldList();
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage = 'Network error loading work order.';
        console.error('CarrierOrderDetails load error:', err);
      },
    });
  }

  private buildFieldList(): void {
    this.fields = this.dynamicConfig.map((col) => {
      const raw = this.rowData[col.db_field];

      return {
        dbField: col.db_field,
        headerName: col.header_name,
        inputType: col.input_type,
        isEditable: col.is_editable,
        value: raw,
        displayValue: this.formatValue(col, raw),
        modalConfig: (col as any).modal_config,
        configItem: col,
      };
    });

    this.groupedFields = this.buildGroups(this.fields);
    this.summaryItems = this.buildSummaryItems(this.fields);
    this.editableFieldCount = this.fields.filter((field) => this.isInlineEditable(field)).length;
  }

  private formatValue(col: DynamicGridConfigItem, raw: any): string {
    if (raw === null || raw === undefined || raw === '') return 'Not provided';

    if (col.input_type === 'checkbox') {
      return this.isChecked(raw) ? 'Yes' : 'No';
    }

    if (col.input_type === 'date') {
      return this.formatDate(raw);
    }

    if (col.input_type === 'datetime') {
      return this.formatDateTime(raw);
    }

    return String(raw);
  }

  private formatDate(raw: any): string {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);

    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private formatDateTime(raw: any): string {
    const text = String(raw).trim();
    const normalizedText = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(text)
      ? text.replace(' ', 'T')
      : text;
    const date = raw instanceof Date ? raw : new Date(normalizedText);

    if (Number.isNaN(date.getTime())) return text;

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  private resolveUpdateTarget(col: DynamicGridConfigItem): DynamicGridUpdateTarget | undefined {
    const configured = col.update_target;

    if (configured?.table && configured?.column) {
      const match_column = configured.match_column ?? configured.primary_key;
      return {
        table: configured.table,
        column: configured.column,
        ...(match_column ? { match_column } : {}),
        ...(configured.scope_column ? { scope_column: configured.scope_column } : {}),
      };
    }

    if (col.db_field.startsWith('v_lading_site_checks.')) {
      return {
        table: 'v_lading_site_checks',
        column: col.db_field.split('.')[1],
        match_column: 'lading_uuid',
        scope_column: 'contact_type',
      };
    }

    return undefined;
  }

  private buildGroups(fields: FieldDisplay[]): FieldGroup[] {
    const groups = new Map<string, FieldGroup>();

    for (const field of fields) {
      if (field.inputType === 'modal') continue;

      const meta = this.getGroupMeta(field);
      const group = groups.get(meta.key) || {
        ...meta,
        fields: [],
      };

      group.fields.push(field);
      groups.set(meta.key, group);
    }

    const order = ['operations', 'schedule', 'locations', 'contacts', 'references', 'notes', 'details'];

    return order
      .map((key) => groups.get(key))
      .filter((group): group is FieldGroup => !!group && group.fields.length > 0);
  }

  private buildSummaryItems(fields: FieldDisplay[]): SummaryItem[] {
    const candidates = [
      this.findFieldValue(fields, ['status']),
      this.findFieldValue(fields, ['pickup', 'appointment', 'date']),
      this.findFieldValue(fields, ['delivery', 'appointment', 'date']),
      this.findFieldValue(fields, ['carrier', 'name']),
      this.findFieldValue(fields, ['technician']),
      this.findFieldValue(fields, ['pro', 'reference']),
      this.findFieldValue(fields, ['bol']),
    ].filter((item): item is SummaryItem => !!item);

    const deduped: SummaryItem[] = [];
    const seen = new Set<string>();

    for (const item of candidates) {
      const key = `${item.label}::${item.value}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(item);
      if (deduped.length === 4) break;
    }

    if (deduped.length > 0) return deduped;

    return [
      {
        label: 'Visible fields',
        value: String(this.fields.length),
        icon: 'fa-solid fa-table-cells-large',
      },
      {
        label: 'Editable fields',
        value: String(this.fields.filter((field) => this.isInlineEditable(field)).length),
        icon: 'fa-solid fa-pen-to-square',
      },
    ];
  }

  private findFieldValue(fields: FieldDisplay[], keywords: string[]): SummaryItem | null {
    const field = fields.find((item) => {
      const haystack = `${item.headerName} ${item.dbField}`.toLowerCase();
      return keywords.every((word) => haystack.includes(word)) && item.displayValue !== 'Not provided';
    });

    if (!field) return null;

    return {
      label: field.headerName,
      value: field.displayValue,
      icon: this.getFieldIcon(field),
    };
  }

  private getGroupMeta(field: FieldDisplay): Omit<FieldGroup, 'fields'> {
    const key = `${field.headerName} ${field.dbField}`.toLowerCase();

    if (this.matchesAny(key, ['status', 'priority', 'service', 'mode'])) {
      return {
        key: 'operations',
        title: 'Operations',
        description: 'Live state, service, and operational attributes.',
        icon: 'fa-solid fa-wave-square',
        accent: 'blue',
      };
    }

    if (this.matchesAny(key, ['pickup', 'delivery', 'date', 'time', 'eta', 'appointment'])) {
      return {
        key: 'schedule',
        title: 'Schedule',
        description: 'Pickup, delivery, and timing-related information.',
        icon: 'fa-solid fa-calendar-check',
        accent: 'amber',
      };
    }

    if (this.matchesAny(key, ['address', 'city', 'state', 'zip', 'location', 'origin', 'destination'])) {
      return {
        key: 'locations',
        title: 'Locations',
        description: 'Origin, destination, and address details.',
        icon: 'fa-solid fa-route',
        accent: 'emerald',
      };
    }

    if (this.matchesAny(key, ['contact', 'carrier', 'shipper', 'consignee', 'driver', 'technician', 'phone', 'email'])) {
      return {
        key: 'contacts',
        title: 'People & Contacts',
        description: 'Carrier, customer, and assigned contact information.',
        icon: 'fa-solid fa-users',
        accent: 'rose',
      };
    }

    if (this.matchesAny(key, ['reference', 'pro', 'bol', 'po', 'invoice', 'order', 'uuid', 'number', 'id'])) {
      return {
        key: 'references',
        title: 'References',
        description: 'Tracking numbers, identifiers, and linked references.',
        icon: 'fa-solid fa-fingerprint',
        accent: 'blue',
      };
    }

    if (this.matchesAny(key, ['note', 'comment', 'memo', 'instruction', 'description'])) {
      return {
        key: 'notes',
        title: 'Notes',
        description: 'Descriptions, instructions, and free-form text.',
        icon: 'fa-solid fa-note-sticky',
        accent: 'amber',
      };
    }

    return {
      key: 'details',
      title: 'Additional Details',
      description: 'Remaining work order attributes not captured elsewhere.',
      icon: 'fa-solid fa-layer-group',
      accent: 'slate',
    };
  }

  private matchesAny(value: string, terms: string[]): boolean {
    return terms.some((term) => value.includes(term));
  }

  isInlineEditable(field: FieldDisplay): boolean {
    return field.isEditable && field.inputType !== 'modal' && field.inputType !== 'readonly_text';
  }

  startEdit(field: FieldDisplay): void {
    if (!this.isInlineEditable(field)) return;

    this.editingField = field.dbField;
    this.editValue = field.inputType === 'checkbox' ? this.isChecked(field.value) : field.value ?? '';
  }

  cancelEdit(): void {
    this.editingField = null;
    this.editValue = '';
  }

  toggleCheckbox(field: FieldDisplay): void {
    if (field.inputType !== 'checkbox' || !this.isInlineEditable(field) || this.togglingField === field.dbField) {
      return;
    }

    const previousValue = field.value;
    const previousDisplayValue = field.displayValue;
    const nextValue = !this.isChecked(field.value);

    this.togglingField = field.dbField;
    this.rowData[field.dbField] = nextValue;
    field.value = nextValue;
    field.displayValue = this.formatValue(field.configItem, nextValue);
    this.summaryItems = this.buildSummaryItems(this.fields);

    const update_target = this.resolveUpdateTarget(field.configItem);

    this.cwoService.updateDynamicWorkOrderCell({
      action: 'update_cell',
      entity_target: 'work_orders',
      identity_scope: this.identityScope,
      contact_uuid: this.selectedCarrierContactUuid || undefined,
      row_uuid: this.ladingUuid,
      db_field: field.configItem.db_field,
      new_value: nextValue,
      ...(update_target ? { update_target } : {}),
    }).subscribe({
      next: (resp) => {
        this.togglingField = null;

        if (!resp || resp.success !== true) {
          this.rowData[field.dbField] = previousValue;
          field.value = previousValue;
          field.displayValue = previousDisplayValue;
          this.summaryItems = this.buildSummaryItems(this.fields);

          if ((resp?.message || '').toLowerCase().includes('strictly read-only')) {
            field.isEditable = false;
            field.configItem.is_editable = false;
          }

          this.toastr.error(resp?.message || 'Update rejected.');
        }
      },
      error: (err) => {
        this.togglingField = null;
        this.rowData[field.dbField] = previousValue;
        field.value = previousValue;
        field.displayValue = previousDisplayValue;
        this.summaryItems = this.buildSummaryItems(this.fields);
        this.toastr.error('Network error saving update.');
        console.error('Checkbox update failed:', err);
      },
    });
  }

  saveEdit(field: FieldDisplay): void {
    if (this.editValue === field.value) {
      this.cancelEdit();
      return;
    }

    this.saving = true;

    const newValue = field.inputType === 'checkbox'
      ? !!this.editValue
      : field.inputType === 'date' && (this.editValue === '' || this.editValue === undefined)
        ? null
        : this.editValue;

    const update_target = this.resolveUpdateTarget(field.configItem);

    this.cwoService.updateDynamicWorkOrderCell({
      action: 'update_cell',
      entity_target: 'work_orders',
      identity_scope: this.identityScope,
      contact_uuid: this.selectedCarrierContactUuid || undefined,
      row_uuid: this.ladingUuid,
      db_field: field.configItem.db_field,
      new_value: newValue,
      ...(update_target ? { update_target } : {}),
    }).subscribe({
      next: (resp) => {
        this.saving = false;

        if (!resp || resp.success !== true) {
          this.toastr.error(resp?.message || 'Update rejected.');
          return;
        }

        this.rowData[field.dbField] = newValue;
        field.value = newValue;
        field.displayValue = this.formatValue(field.configItem, newValue);
        this.summaryItems = this.buildSummaryItems(this.fields);

        this.toastr.success(`${field.headerName} updated.`);
        this.cancelEdit();
      },
      error: (err) => {
        this.saving = false;
        this.toastr.error('Network error saving update.');
        console.error('Cell update failed:', err);
      },
    });
  }

  openModal(field: FieldDisplay): void {
    if (!field.modalConfig?.modal_type) return;

    const ref = this.modalDispatcher.dispatch(
      this.ladingUuid,
      this.rowData,
      field.modalConfig,
      field.headerName,
      this.selectedCarrierContactUuid || undefined,
    );

    if (ref) {
      ref.afterClosed().subscribe((result: any) => {
        if (result?.refreshGrid) {
          this.loadData();
        }
      });
    }
  }

  isChecked(val: any): boolean {
    return val === true || val === 'true' || val === 't' || val === '1' || val === 1;
  }

  getModalLabel(field: FieldDisplay): string {
    const mt = field.modalConfig?.modal_type;
    if (mt === 'status_timeline') return 'Open timeline';
    if (mt === 'technician') return 'Manage technicians';
    return field.modalConfig?.dialog_title || 'Open action';
  }

  getModalFields(): FieldDisplay[] {
    return this.fields.filter((field) => field.inputType === 'modal' && field.modalConfig?.modal_type);
  }

  getModalIcon(field: FieldDisplay): string {
    const mt = field.modalConfig?.modal_type;
    if (mt === 'status_timeline') return 'fa-solid fa-clock-rotate-left';
    if (mt === 'technician') return 'fa-solid fa-user-gear';
    return 'fa-solid fa-up-right-from-square';
  }

  getFieldIcon(field: FieldDisplay): string {
    const key = `${field.headerName} ${field.dbField}`.toLowerCase();

    if (this.matchesAny(key, ['status'])) return 'fa-solid fa-signal';
    if (this.matchesAny(key, ['pickup', 'delivery', 'date', 'time'])) return 'fa-solid fa-calendar-day';
    if (this.matchesAny(key, ['address', 'location', 'city', 'state'])) return 'fa-solid fa-location-dot';
    if (this.matchesAny(key, ['contact', 'carrier', 'shipper', 'consignee', 'technician'])) return 'fa-solid fa-user';
    if (this.matchesAny(key, ['reference', 'pro', 'bol', 'po', 'number', 'id'])) return 'fa-solid fa-hashtag';
    if (this.matchesAny(key, ['note', 'comment', 'memo'])) return 'fa-solid fa-note-sticky';
    if (field.inputType === 'checkbox') return 'fa-solid fa-circle-check';
    if (field.inputType === 'date') return 'fa-solid fa-calendar';

    return 'fa-solid fa-circle-dot';
  }

  getFieldTone(field: FieldDisplay): string {
    if (field.inputType === 'modal') return 'accent';
    if (field.inputType === 'checkbox' && this.isChecked(field.value)) return 'success';
    if (field.displayValue === 'Not provided') return 'muted';
    return 'default';
  }

  getFieldTypeLabel(field: FieldDisplay): string {
    if (field.inputType === 'readonly_text') return 'Read only';
    if (field.inputType === 'checkbox') return 'Boolean';
    return field.inputType.charAt(0).toUpperCase() + field.inputType.slice(1);
  }

  shouldSpanWide(field: FieldDisplay): boolean {
    const key = `${field.headerName} ${field.dbField}`.toLowerCase();
    return this.matchesAny(key, ['address', 'note', 'comment', 'memo', 'instruction', 'description']);
  }

  trackByField(_: number, field: FieldDisplay): string {
    return field.dbField;
  }

  trackByGroup(_: number, group: FieldGroup): string {
    return group.key;
  }
}
